import { inject, InjectionToken } from '@angular/core';
import { IProductDataV3, Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Nexus } from '@tomandco/nexus-api-types';
import { Subject } from 'rxjs';

export const GROUPED_ITEM_DATA_TOKEN = new InjectionToken<Nexus.Lib.Catalog.Type.IProductDataGroupedOption>('GROUPED_ITEM_DATA_TOKEN');

export class GroupedItem {
    readonly data = inject(GROUPED_ITEM_DATA_TOKEN);
    readonly change$ = new Subject<void>();
    readonly product: Product<IProductDataV3>;

    protected _qty = 0;

    private readonly _position: number;
    private readonly productResource = inject(ProductResource);

    get id(): number {
        return this.data.id;
    }

    get position(): number {
        return this._position;
    }

    /**
     * Item selection state - computed from qty
     * Selected when qty > 0
     */
    get selected(): boolean {
        return this._qty > 0;
    }

    set selected(value: boolean) {
        if (value && this._qty === 0) {
            this.qty = this.data.qty || 1;
        } else if (!value && this._qty > 0) {
            this.qty = 0;
        }
    }

    get qty(): number {
        return this._qty;
    }

    set qty(value: number) {
        // Sanitize input
        let newQty = Number(value);
        if (isNaN(newQty) || newQty < 0) {
            newQty = 0;
        }
        newQty = Math.round(newQty);
        if (this._qty !== newQty) {
            this._qty = newQty;
            this.change$.next();
        }
    }

    get isOutOfStock(): boolean {
        return this.product.isOutOfStock;
    }

    get isQtySalable(): boolean {
        return this.product.isSalable(this._qty);
    }

    get price(): number {
        return this.product.price || 0;
    }

    get rowPrice(): number {
        return this.price * this._qty;
    }

    constructor() {
        this._position = this.data.position ?? 0;
        this.product = this.productResource.get(this.data.id) as Product<IProductDataV3>;
        if (this.data.qty) {
            this._qty = this.data.qty;
        }
    }

    destroy(): void {
        this.change$.complete();
    }
}
